import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState, type DragEvent, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { apiClient, responseData } from "@/api/client";
import type { Account, BankCurrency, Domain, ImportBatch, PageResponse, ParsedRow, Source } from "@/api/types";
import { useWorkspace } from "@/app/workspace";
import { Empty, Panel } from "@/components/Page";
import { Pagination } from "@/components/Pagination";
import { statusClass } from "@/components/format";
import { NormalizedDataTable } from "@/components/NormalizedDataTable";
import { useConfirmDialog } from "@/components/ConfirmDialog";
import { useToast } from "@/components/Toast";
import { bankSearch, masterSearch } from "@/components/DomainContext";

export interface ImportWorkspaceConfig {
  domain: Domain;
  accountKind: Domain;
  apiBase: string;
  basePath: string;
  noun: string;
}

const cardFundingLabel = (funding?: "DEBIT" | "CREDIT") => funding === "DEBIT" ? "débito" : funding === "CREDIT" ? "crédito" : "";

function importKindLabel(batch: ImportBatch) {
  if (batch.loadType === "LIQUIDACION") {
    const funding = cardFundingLabel(batch.cardFunding);
    return `Liquidación${funding ? ` ${funding}` : ""} · ${batch.cardBrand?.name ?? "Tarjeta"}`;
  }
  if (batch.loadType === "LIBRO_MAYOR" && batch.cardBrand) return `Libro Mayor · ${batch.cardBrand.name}`;
  if (batch.loadType === "EXTRACTO") return `Extracto · ${batch.bank?.name ?? "Banco"} · ${batch.currency ?? "ARS"}`;
  if (batch.loadType === "LIBRO_MAYOR") return `Libro Mayor · ${batch.bank?.name ?? "Banco"} · ${batch.currency ?? "ARS"}`;
  return batch.sourceDefinition.name;
}

const importNettingCounts = (batch: ImportBatch) => {
  const summary = batch.summary ?? {};
  const source = Number(summary.sourceRecordCount ?? batch.totalRows);
  const effective = Number(summary.effectiveRecordCount ?? batch.totalRows);
  const netted = Number(summary.nettedRecordCount ?? Math.max(0, source - effective));
  return { source, effective, netted };
};

const importRecordCountLabel = (batch: ImportBatch) => {
  const counts = importNettingCounts(batch);
  if (!counts.netted) return `${batch.totalRows} ${batch.totalRows === 1 ? "movimiento" : "movimientos"}`;
  const destination = batch.status === "PREVIEW" ? "a incorporar" : "incorporados";
  return `${counts.source} leídos · ${counts.netted} neteados · ${counts.effective} ${destination}`;
};

function ImportNettingSummary({ batch }: { batch: ImportBatch }) {
  const counts = importNettingCounts(batch);
  const records = Array.isArray(batch.summary?.nettedRecords) ? batch.summary.nettedRecords : [];
  if (!counts.netted) return null;
  return (
    <details className="import-netting-summary">
      <summary>{importRecordCountLabel(batch)} <span>Ver filas excluidas</span></summary>
      <div className="import-netting-summary__body">
        <p>Estas filas se neutralizaron entre sí antes de crear movimientos operativos.</p>
        <ul>
          {records.map((record, index) => {
            const amountLabel = Number(record.debit) !== 0 ? `Debe ${record.debit}` : `Haber ${record.credit}`;
            return <li key={`${record.sheetName}-${record.rowNumber}-${index}`}>
              <strong>Fila {record.rowNumber}{record.voucherType ? ` · ${record.voucherType}` : ""} · {record.description || "Sin descripción"}</strong>
              <span>{amountLabel}{record.reference ? ` · Ref. ${record.reference}` : ""}</span>
              <small>{record.reason}{record.counterpartRowNumber ? ` Contraparte: fila ${record.counterpartRowNumber}.` : ""}</small>
            </li>;
          })}
        </ul>
      </div>
    </details>
  );
}

const previousCalendarMonth = () => {
  const now = new Date();
  const date = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

const MONTH_NAMES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const dottedExtensions = (values: string[]) => values.map((value) => value.startsWith(".") ? value.toLowerCase() : `.${value.toLowerCase()}`);

const importRangeError = (from: string, to: string) => {
  const periodPattern = /^\d{4}-(0[1-9]|1[0-2])$/;
  if (!periodPattern.test(from) || !periodPattern.test(to)) return "Completá los períodos desde y hasta.";
  return from > to ? "El período desde no puede ser posterior al período hasta." : "";
};

const listImports = (config: ImportWorkspaceConfig, input: { hotelId: string; periodFrom: string; periodTo: string; page: number; status: string; q: string; bankId: string; currency: string }): Promise<PageResponse<ImportBatch>> =>
  config.domain === "BANK"
    ? responseData(apiClient.GET("/api/v1/banking/imports", { params: { query: { hotelId: input.hotelId, periodFrom: input.periodFrom, periodTo: input.periodTo, q: input.q || undefined, page: String(input.page), pageSize: "25", status: input.status, bankId: input.bankId || undefined, currency: input.currency === "ARS" || input.currency === "USD" ? input.currency : undefined } } }))
    : responseData(apiClient.GET("/api/v1/cards/imports", { params: { query: { hotelId: input.hotelId, periodFrom: input.periodFrom, periodTo: input.periodTo, q: input.q || undefined, page: String(input.page), pageSize: "25", status: input.status } } }));

const importDetail = (domain: Domain, id: string): Promise<ImportBatch> => domain === "BANK"
  ? responseData(apiClient.GET("/api/v1/banking/imports/{id}", { params: { path: { id } } }))
  : responseData(apiClient.GET("/api/v1/cards/imports/{id}", { params: { path: { id } } }));

const importRows = (domain: Domain, id: string, page: number): Promise<PageResponse<ParsedRow>> => domain === "BANK"
  ? responseData(apiClient.GET("/api/v1/banking/imports/{id}/rows", { params: { path: { id }, query: { page: String(page), pageSize: "25" } } }))
  : responseData(apiClient.GET("/api/v1/cards/imports/{id}/rows", { params: { path: { id }, query: { page: String(page), pageSize: "25" } } }));

const deleteImport = (domain: Domain, id: string) => domain === "BANK"
  ? responseData(apiClient.DELETE("/api/v1/banking/imports/{id}", { params: { path: { id } } }))
  : responseData(apiClient.DELETE("/api/v1/cards/imports/{id}", { params: { path: { id } } }));

const downloadImportFile = async (apiBase: string, id: string, fileName: string) => {
  const response = await fetch(`/api/v1${apiBase}/imports/${encodeURIComponent(id)}/file`, { credentials: "include" });
  if (response.status === 401) {
    window.location.assign("/acceso");
    throw new Error("La sesión expiró.");
  }
  if (!response.ok) {
    const payload = await response.clone().json().catch(() => null) as { message?: string | string[] } | null;
    const message = Array.isArray(payload?.message) ? payload.message.join(" ") : payload?.message;
    throw new Error(message || "No se pudo descargar el archivo.");
  }
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

const DownloadFileIcon = () => <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 3v11m0 0 4-4m-4 4-4-4M4 17v3h16v-3" /></svg>;
const ViewDetailIcon = () => <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" /><circle cx="12" cy="12" r="2.5" /></svg>;
const TrashIcon = () => <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v5m4-5v5" /></svg>;

const confirmImport = (domain: Domain, id: string): Promise<ImportBatch> => domain === "BANK"
  ? responseData(apiClient.POST("/api/v1/banking/imports/{id}/confirm", { params: { path: { id } } }))
  : responseData(apiClient.POST("/api/v1/cards/imports/{id}/confirm", { params: { path: { id } } }));
function ImportHistoryList({ config, onSelect, onNewImport }: { config: ImportWorkspaceConfig; onSelect(id: string): void; onNewImport?(): void }) {
  const confirmDialog = useConfirmDialog();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { hotelId, period } = useWorkspace();
  const [params, setParams] = useSearchParams();
  const page = Number(params.get("importPage") ?? 1);
  const status = params.get("importStatus") ?? "all";
  const q = params.get("importQ") ?? "";
  const periodFrom = params.get("importPeriodFrom") ?? period;
  const periodTo = params.get("importPeriodTo") ?? period;
  const masterId = params.get("masterId") ?? "";
  const bankId = params.get("bankId") ?? (config.domain === "BANK" ? masterId : "");
  const currency = params.get("currency") ?? "";
  const [search, setSearch] = useState(q);
  const [from, setFrom] = useState(periodFrom);
  const [to, setTo] = useState(periodTo);
  const rangeError = importRangeError(from, to);
  const appliedRangeError = importRangeError(periodFrom, periodTo);
  const batches = useQuery({
    queryKey: ["domain-imports", config.domain, hotelId, periodFrom, periodTo, page, status, q, masterId, bankId, currency],
    queryFn: () => listImports(config, { hotelId, periodFrom, periodTo, page, status, q, bankId, currency }),
    enabled: Boolean(hotelId) && !appliedRangeError,
    refetchInterval: (query) => query.state.data?.items.some((item) => ["QUEUED", "PROCESSING"].includes(item.status)) ? 2000 : false
  });
  const updateFilters = (values: Record<string, string>) => {
    const next = new URLSearchParams(params);
    Object.entries(values).forEach(([key, value]) => value ? next.set(key, value) : next.delete(key));
    next.set("importPage", "1");
    setParams(next);
  };
  const removePreview = useMutation({
    mutationFn: (id: string) => deleteImport(config.domain, id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["domain-imports", config.domain] }),
        queryClient.invalidateQueries({ queryKey: ["workflow", config.domain] }),
        ...(config.domain === "BANK" ? [
          queryClient.invalidateQueries({ queryKey: ["bank-account-overview"] }),
          queryClient.invalidateQueries({ queryKey: ["bank-balance-summary"] }),
          queryClient.invalidateQueries({ queryKey: ["bank-movements"] }),
          queryClient.invalidateQueries({ queryKey: ["bank-ledger"] }),
          queryClient.invalidateQueries({ queryKey: ["run", "bank"] }),
          queryClient.invalidateQueries({ queryKey: ["groups", "bank"] }),
          queryClient.invalidateQueries({ queryKey: ["bank-review-summary"] }),
        ] : [
          queryClient.invalidateQueries({ queryKey: ["card-brand-overview"] }),
          queryClient.invalidateQueries({ queryKey: ["card-settlements"] }),
          queryClient.invalidateQueries({ queryKey: ["card-ledger"] }),
          queryClient.invalidateQueries({ queryKey: ["run", "card"] }),
          queryClient.invalidateQueries({ queryKey: ["groups", "card"] }),
          queryClient.invalidateQueries({ queryKey: ["review-groups"] }),
        ]),
      ]);
      toast.success("Carga eliminada. Las conciliaciones y los pendientes ya fueron actualizados.");
    },
    onError: (reason) => toast.error(reason, { fallback: "No se pudo eliminar la carga." })
  });
  const downloadFile = useMutation({
    mutationFn: (batch: ImportBatch) => downloadImportFile(config.apiBase, batch.id, batch.sourceFile?.originalName ?? "archivo"),
    onError: (reason) => toast.error(reason, { fallback: "No se pudo descargar el archivo." }),
  });
  const confirmRemoval = async (batch: ImportBatch) => {
    const name = batch.sourceFile?.originalName ?? "esta carga";
    const failed = batch.status === "FAILED";
    const confirmed = ["CONFIRMED", "CONCILIATED"].includes(batch.status);
    const accepted = await confirmDialog({
      title: confirmed ? "Eliminar carga confirmada" : failed ? "Eliminar carga fallida" : "Eliminar vista previa",
      description: confirmed
        ? `Se eliminarán ${name} y sus movimientos o asientos. Todas las conciliaciones que usen esos registros se desvincularán y sus contrapartes volverán a quedar pendientes. Esta acción no se puede deshacer.`
        : `Se eliminarán ${name} y todos sus registros extraídos. Esta acción no se puede deshacer.`,
      confirmLabel: "Eliminar carga",
      tone: "danger",
    });
    if (accepted) removePreview.mutate(batch.id);
  };
  return (
      <div className="import-history-list">
        <form className="filter-bar import-history-filters" onSubmit={(event) => { event.preventDefault(); if (!rangeError) updateFilters({ importPeriodFrom: from, importPeriodTo: to, importQ: search }); }}>
          <label className="import-history-filters__period"><span>Desde</span><input type="month" value={from} onChange={(event) => setFrom(event.target.value)} /></label>
          <label className="import-history-filters__period"><span>Hasta</span><input type="month" value={to} onChange={(event) => setTo(event.target.value)} /></label>
          <input className="import-history-filters__search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar archivo o fuente" aria-label="Buscar cargas" />
          <select className="import-history-filters__status" value={status} onChange={(event) => updateFilters({ importStatus: event.target.value })} aria-label="Estado de carga">
            <option value="all">Todos los estados</option><option value="QUEUED">En cola</option><option value="PROCESSING">Procesando</option><option value="PREVIEW">Vista previa</option><option value="CONFIRMED">Confirmadas</option>{config.domain !== "BANK" && <option value="CONCILIATED">Conciliadas</option>}<option value="FAILED">Fallidas</option>
          </select>
          <div className="import-history-filters__actions">
            <button className="button button--small" disabled={Boolean(rangeError)}>Buscar</button>
            {onNewImport && <button type="button" className="button button--primary button--small" onClick={onNewImport}>Nueva carga</button>}
          </div>
          {rangeError && <span className="import-history-filters__error" role="alert">{rangeError}</span>}
        </form>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Archivo</th><th>Período</th><th>{config.domain === "CARD" ? "Tipo / Marca" : "Tipo / Banco / Cuenta"}</th><th>Progreso</th><th>Filas</th><th>Estado</th><th aria-label="Acciones" /></tr></thead>
            <tbody>{batches.data?.items.map((batch) => (
              <tr key={batch.id}>
                <td><button type="button" className="table-link import-history-list__file" onClick={() => onSelect(batch.id)}>{batch.sourceFile?.originalName ?? "Archivo"}</button></td>
                <td className="mono">{batch.period}</td>
                <td>{importKindLabel(batch)}</td><td className="mono">{batch.progress}%</td><td className="mono">{batch.filteredRows ?? batch.totalRows}</td>
                <td><span className={`status status--${statusClass(batch.status)}`}>{batch.status}</span></td>
                <td className="table-actions"><div className="table-icon-actions">
                  <button type="button" className="table-icon-button" aria-label={`Ver detalle de ${batch.sourceFile?.originalName ?? "carga"}`} title={`Ver detalle de ${batch.sourceFile?.originalName ?? "carga"}`} onClick={() => onSelect(batch.id)}><ViewDetailIcon /></button>
                  {batch.sourceFile && <button type="button" className="table-icon-button" aria-label={`Descargar ${batch.sourceFile.originalName}`} title={`Descargar ${batch.sourceFile.originalName}`} onClick={() => downloadFile.mutate(batch)} disabled={downloadFile.isPending}><DownloadFileIcon /></button>}
                  {["PREVIEW", "FAILED", "CONFIRMED"].includes(batch.status) || (config.domain === "CARD" && batch.status === "CONCILIATED") ? <button type="button" className="table-icon-button table-icon-button--danger" aria-label={`Eliminar ${batch.sourceFile?.originalName ?? "carga"}`} title={`Eliminar ${batch.sourceFile?.originalName ?? "carga"}`} onClick={() => void confirmRemoval(batch)} disabled={removePreview.isPending}><TrashIcon /></button> : null}
                </div></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        {!batches.isLoading && !batches.data?.items.length && <Empty>No hay cargas para el rango seleccionado.</Empty>}
        <Pagination meta={batches.data?.meta} onPageChange={(nextPage) => { const next = new URLSearchParams(params); next.set("importPage", String(nextPage)); setParams(next); }} />
      </div>
  );
}

export function ImportWorkspace({ config }: { config: ImportWorkspaceConfig }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const masterId = params.get("masterId") ?? "";
  const bankId = params.get("bankId") ?? (config.domain === "BANK" ? masterId : "");
  const currency = params.get("currency") ?? "";
  const contextSearch = config.domain === "BANK" ? bankSearch(bankId, currency) : masterSearch(masterId);
  return <Panel title="Historial de cargas"><ImportHistoryList config={config} onSelect={(id) => navigate(`${config.basePath}/cargas/${id}${contextSearch}`)} /></Panel>;
}

function ImportConfirmationAction({
  config,
  id,
  variant,
  onConfirmed,
}: {
  config: ImportWorkspaceConfig;
  id: string;
  variant: "header" | "body";
  onConfirmed?(): void;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const masterId = params.get("masterId") ?? "";
  const bankId = params.get("bankId") ?? (config.domain === "BANK" ? masterId : "");
  const currency = params.get("currency") ?? "";
  const contextSearch = config.domain === "BANK" ? bankSearch(bankId, currency) : masterSearch(masterId);
  const confirm = useMutation({
    mutationFn: () => confirmImport(config.domain, id),
    onSuccess: async () => {
      toast.success("Carga confirmada.");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["domain-import", config.domain, id] }),
        queryClient.invalidateQueries({ queryKey: ["domain-imports", config.domain] }),
        queryClient.invalidateQueries({ queryKey: ["workflow", config.domain] }),
        queryClient.invalidateQueries({ queryKey: ["card-brand-overview"] }),
        ...(config.domain === "BANK" ? [
          queryClient.invalidateQueries({ queryKey: ["bank-movements"] }),
          queryClient.invalidateQueries({ queryKey: ["bank-ledger"] }),
          queryClient.invalidateQueries({ queryKey: ["run", "bank"] }),
          queryClient.invalidateQueries({ queryKey: ["groups", "bank"] }),
        ] : [])
      ]);
      onConfirmed?.();
      navigate(`${config.basePath}/${config.domain === "BANK" ? "movimientos" : config.domain === "CARD" ? "liquidaciones" : "reservas"}${contextSearch}`);
    },
    onError: async (reason) => {
      toast.error(reason, { fallback: "No se pudo confirmar la carga." });
      await queryClient.invalidateQueries({ queryKey: ["domain-import", config.domain, id] });
    }
  });
  return (
    <div className={variant === "header" ? "import-history-modal__preview-action" : "confirm-bar"}>
      <span><strong>La vista previa está lista</strong><small>Confirmá para incorporar las filas válidas.</small></span>
      <button className="button button--primary" onClick={() => confirm.mutate()} disabled={confirm.isPending}>Confirmar carga</button>
    </div>
  );
}

export function ImportHistoryModal({
  config,
  open,
  suspended = false,
  batchId,
  onSelectBatch,
  onNewImport,
  onBack,
  onClose,
}: {
  config: ImportWorkspaceConfig;
  open: boolean;
  suspended?: boolean;
  batchId: string;
  onSelectBatch(id: string): void;
  onNewImport(): void;
  onBack(): void;
  onClose(): void;
}) {
  const titleId = useId();
  const dialog = useRef<HTMLElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const closeHandler = useRef(onClose);
  closeHandler.current = onClose;
  const suspendedRef = useRef(suspended);
  suspendedRef.current = suspended;
  const selectedBatch = useQuery({
    queryKey: ["domain-import", config.domain, batchId],
    queryFn: () => importDetail(config.domain, batchId),
    enabled: open && Boolean(batchId),
    refetchInterval: (query) => ["QUEUED", "PROCESSING"].includes(query.state.data?.status ?? "") ? 2000 : false
  });
  const detailBatch = selectedBatch.data;
  const detailContext = detailBatch
    ? [importKindLabel(detailBatch), detailBatch.period].filter(Boolean).join(" · ")
    : "Cargas";

  useEffect(() => {
    if (!open) return;
    returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => { if (!suspendedRef.current) closeButton.current?.focus(); });
    const keydown = (event: KeyboardEvent) => {
      if (suspendedRef.current) return;
      if (event.key === "Escape") {
        event.preventDefault();
        closeHandler.current();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), select:not(:disabled), input:not(:disabled), a[href]') ?? []);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", keydown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", keydown);
      const target = returnFocus.current;
      const menuButton = target?.closest(".domain-context-card__menu")?.querySelector<HTMLElement>(".context-kebab");
      if (target?.closest("[hidden]")) menuButton?.focus();
      else target?.focus();
    };
  }, [open]);

  if (!open) return null;
  return (
    <div className="modal-backdrop import-history-backdrop" aria-hidden={suspended || undefined} inert={suspended} onMouseDown={(event) => { if (!suspended && event.target === event.currentTarget) onClose(); }}>
      <section ref={dialog} className="import-modal import-history-modal" role="dialog" aria-modal={!suspended} aria-labelledby={titleId}>
        <header className={batchId ? "import-history-modal__detail-header" : undefined}>
          {batchId && <button type="button" className="back-link import-history-back import-history-modal__back" onClick={onBack}>← Volver al historial</button>}
          <div className="import-history-modal__heading">
            <small>{batchId ? detailContext : "Cargas"}</small>
            <h2 id={titleId}>{batchId ? detailBatch?.sourceFile?.originalName ?? "Cargando carga…" : "Historial de cargas"}</h2>
            {batchId && detailBatch && <p>{importRecordCountLabel(detailBatch)}</p>}
          </div>
          <div className="import-history-modal__header-actions">
            {batchId && detailBatch && <span className={`status status--${statusClass(detailBatch.status)}`}>{detailBatch.status}</span>}
            <button ref={closeButton} type="button" className="modal-close" onClick={onClose} aria-label="Cerrar historial de cargas">×</button>
          </div>
          {batchId && detailBatch?.status === "PREVIEW" && <ImportConfirmationAction config={config} id={batchId} variant="header" onConfirmed={onClose} />}
        </header>
        <div className={`import-history-modal__body${batchId ? " import-history-modal__body--detail" : ""}`}>
          {batchId
            ? <ImportDetail config={config} id={batchId} modal onBack={onBack} />
            : <ImportHistoryList config={config} onSelect={onSelectBatch} onNewImport={onNewImport} />}
        </div>
      </section>
    </div>
  );
}

function CardFileDrop({
  label,
  file,
  extensions,
  brandSelected,
  disabled,
  onChoose,
  onClear,
}: {
  label: string;
  file?: File;
  extensions: string[];
  brandSelected: boolean;
  disabled: boolean;
  onChoose(file?: File): void;
  onClear?(): void;
}) {
  const [dragging, setDragging] = useState(false);
  const accepted = extensions.join(",");
  const formatNames = extensions.map((extension) => extension.slice(1).toUpperCase()).join(" o ");
  const formatSuffix = extensions.length ? ` (${extensions.join(" / ")})` : "";
  return <div className="card-file-slot">
    <label className={`drop-zone${file ? " drop-zone--ready" : ""}${dragging ? " drop-zone--dragging" : ""}`} aria-disabled={disabled} onDragEnter={(event) => { event.preventDefault(); event.stopPropagation(); event.dataTransfer.dropEffect = disabled ? "none" : "copy"; if (!disabled) setDragging(true); }} onDragOver={(event) => { event.preventDefault(); event.stopPropagation(); event.dataTransfer.dropEffect = disabled ? "none" : "copy"; }} onDragLeave={(event) => { event.preventDefault(); event.stopPropagation(); if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return; setDragging(false); }} onDrop={(event) => { event.preventDefault(); event.stopPropagation(); setDragging(false); if (!disabled) onChoose(event.dataTransfer.files[0]); }}>
      <input type="file" disabled={disabled} accept={accepted || undefined} aria-label={label} onChange={(event) => { onChoose(event.target.files?.[0]); event.currentTarget.value = ""; }} />
      <span className="drop-zone__icon">↑</span>
      <strong>{file?.name ?? `${label}${formatSuffix}`}</strong>
      <small>{formatNames ? `${formatNames} · o arrastralo aquí` : brandSelected ? "No hay formatos configurados para esta marca" : "Elegí una marca para ver formatos"}</small>
    </label>
    {file && onClear && !disabled && <button type="button" className="card-file-slot__clear" onClick={onClear} aria-label={`Quitar ${file.name}`}>Quitar archivo</button>}
  </div>;
}

type CardImportSlot = "ledger" | "credit" | "debit";
type CardSlotUploadResult = { slot: CardImportSlot; batch: { id: string } } | { slot: CardImportSlot; error: Error };

function CardDualImportModal({ open, stacked = false, onClose }: { open: boolean; stacked?: boolean; onClose(): void; onPreviewReady?(id: string): void }) {
  const confirmDialog = useConfirmDialog();
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const lockedBrandId = params.get("masterId") ?? "";
  const { hotelId, setPeriod } = useWorkspace();
  const firstField = useRef<HTMLSelectElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const dialog = useRef<HTMLElement>(null);
  const [importPeriod, setImportPeriod] = useState(previousCalendarMonth);
  const [cardBrandId, setCardBrandId] = useState("");
  const [ledgerFile, setLedgerFile] = useState<File>();
  const [creditFile, setCreditFile] = useState<File>();
  const [debitFile, setDebitFile] = useState<File>();
  const [fileError, setFileError] = useState("");
  const [ledgerBatchId, setLedgerBatchId] = useState("");
  const [creditBatchId, setCreditBatchId] = useState("");
  const [debitBatchId, setDebitBatchId] = useState("");
  const [message, setMessage] = useState("");
  const [slotErrors, setSlotErrors] = useState<Record<CardImportSlot, string>>({ ledger: "", credit: "", debit: "" });
  const [slotConflicts, setSlotConflicts] = useState<Record<CardImportSlot, boolean>>({ ledger: false, credit: false, debit: false });
  const [retryingSlot, setRetryingSlot] = useState<CardImportSlot | "">("");
  const [showPreview, setShowPreview] = useState(false);
  const options = useQuery({ queryKey: ["card-import-options"], queryFn: () => responseData(apiClient.GET("/api/v1/cards/imports/configuration")), enabled: open });
  const selectedBrand = options.data?.brands.find((brand) => brand.id === cardBrandId);
  const ledgerExtensions = dottedExtensions(selectedBrand?.ledgerExtensions ?? []);
  const liquidationExtensions = dottedExtensions(selectedBrand?.liquidationExtensions ?? []);
  const ledgerBatch = useQuery({ queryKey: ["new-import", "CARD", ledgerBatchId], queryFn: () => importDetail("CARD", ledgerBatchId), enabled: open && Boolean(ledgerBatchId), refetchInterval: (query) => ["QUEUED", "PROCESSING"].includes(query.state.data?.status ?? "") ? 1500 : false });
  const creditBatch = useQuery({ queryKey: ["new-import", "CARD", creditBatchId], queryFn: () => importDetail("CARD", creditBatchId), enabled: open && Boolean(creditBatchId), refetchInterval: (query) => ["QUEUED", "PROCESSING"].includes(query.state.data?.status ?? "") ? 1500 : false });
  const debitBatch = useQuery({ queryKey: ["new-import", "CARD", debitBatchId], queryFn: () => importDetail("CARD", debitBatchId), enabled: open && Boolean(debitBatchId), refetchInterval: (query) => ["QUEUED", "PROCESSING"].includes(query.state.data?.status ?? "") ? 1500 : false });
  const ledgerPreviewRows = useQuery({ queryKey: ["card-import-preview", ledgerBatchId], queryFn: () => importRows("CARD", ledgerBatchId, 1), enabled: open && showPreview && Boolean(ledgerBatchId) });
  const creditPreviewRows = useQuery({ queryKey: ["card-import-preview", creditBatchId], queryFn: () => importRows("CARD", creditBatchId, 1), enabled: open && showPreview && Boolean(creditBatchId) });
  const debitPreviewRows = useQuery({ queryKey: ["card-import-preview", debitBatchId], queryFn: () => importRows("CARD", debitBatchId, 1), enabled: open && showPreview && Boolean(debitBatchId) });
  const uploadOne = async (file: File, loadType: "LIBRO_MAYOR" | "LIQUIDACION", replace = false, cardFunding?: "DEBIT" | "CREDIT") => {
    const form = new FormData();
    form.set("file", file);
    form.set("hotelId", hotelId);
    form.set("period", importPeriod);
    form.set("cardBrandId", cardBrandId);
    form.set("loadType", loadType);
    if (cardFunding) form.set("cardFunding", cardFunding);
    if (replace) form.set("replaceExisting", "true");
    return responseData(apiClient.POST("/api/v1/cards/imports", {
      body: { hotelId, period: importPeriod, cardBrandId, loadType, cardFunding, replaceExisting: replace ? "true" : undefined, file: file.name },
      bodySerializer: () => form,
    }));
  };
  const uploadSlot = (slot: CardImportSlot, replace = false) => {
    if (slot === "ledger" && ledgerFile) return uploadOne(ledgerFile, "LIBRO_MAYOR", replace);
    if (slot === "credit" && creditFile) return uploadOne(creditFile, "LIQUIDACION", replace, "CREDIT");
    if (slot === "debit" && debitFile) return uploadOne(debitFile, "LIQUIDACION", replace, "DEBIT");
    throw new Error("Adjuntá el archivo antes de cargarlo.");
  };
  const create = useMutation({
    mutationFn: async () => {
      if (!cardBrandId) throw new Error("Seleccioná la marca.");
      const slots = ([
        ledgerFile && !ledgerBatchId ? "ledger" : undefined,
        creditFile && !creditBatchId ? "credit" : undefined,
        debitFile && !debitBatchId ? "debit" : undefined,
      ] satisfies Array<CardImportSlot | undefined>).filter((slot): slot is CardImportSlot => Boolean(slot));
      if (!slots.length) throw new Error("Adjuntá al menos un archivo.");
      return Promise.all(slots.map(async (slot): Promise<CardSlotUploadResult> => {
        try {
          const batch = await uploadSlot(slot);
          return { slot, batch: { id: batch.id } };
        } catch (error) {
          return { slot, error: error instanceof Error ? error : new Error("No se pudo cargar el archivo.") };
        }
      }));
    },
    onSuccess: async (results) => {
      const nextErrors = { ledger: "", credit: "", debit: "" } satisfies Record<CardImportSlot, string>;
      const nextConflicts: Record<CardImportSlot, boolean> = { ledger: false, credit: false, debit: false };
      let uploaded = 0;
      for (const result of results) {
        if ("batch" in result) {
          uploaded += 1;
          if (result.slot === "ledger") setLedgerBatchId(result.batch.id);
          if (result.slot === "credit") setCreditBatchId(result.batch.id);
          if (result.slot === "debit") setDebitBatchId(result.batch.id);
          continue;
        }
        const apiCode = (result.error as Error & { code?: string }).code;
        nextErrors[result.slot] = result.error.message;
        nextConflicts[result.slot] = apiCode === "CONFLICT" || result.error.message.includes("Ya existe una carga");
      }
      setSlotErrors(nextErrors);
      setSlotConflicts(nextConflicts);
      const failed = results.length - uploaded;
      setMessage(uploaded
        ? `${uploaded === 1 ? "Archivo recibido" : `${uploaded} archivos recibidos`}. Estamos preparando ${uploaded === 1 ? "la vista previa" : "las vistas previas"}.${failed ? ` ${failed === 1 ? "Un archivo requiere atención" : `${failed} archivos requieren atención`}.` : ""}`
        : "Revisá los archivos que requieren atención.");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["domain-imports", "CARD"] }),
        queryClient.invalidateQueries({ queryKey: ["workflow", "CARD"] }),
        queryClient.invalidateQueries({ queryKey: ["card-brand-overview"] }),
      ]);
    },
    onError: (error) => {
      setMessage(error.message);
    },
  });
  const confirmLoads = useMutation({
    mutationFn: async () => {
      if (ledgerBatch.data?.status === "PREVIEW") await confirmImport("CARD", ledgerBatchId);
      if (creditBatch.data?.status === "PREVIEW") await confirmImport("CARD", creditBatchId);
      if (debitBatch.data?.status === "PREVIEW") await confirmImport("CARD", debitBatchId);
    },
    onSuccess: async () => {
      setPeriod(importPeriod);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["new-import", "CARD"] }),
        queryClient.invalidateQueries({ queryKey: ["domain-imports", "CARD"] }),
        queryClient.invalidateQueries({ queryKey: ["workflow", "CARD"] }),
        queryClient.invalidateQueries({ queryKey: ["card-brand-overview"] }),
        queryClient.invalidateQueries({ queryKey: ["card-settlements"] }),
        queryClient.invalidateQueries({ queryKey: ["card-ledger"] }),
        queryClient.invalidateQueries({ queryKey: ["run", "card"] }),
        queryClient.invalidateQueries({ queryKey: ["groups", "card"] }),
        queryClient.invalidateQueries({ queryKey: ["review-groups"] }),
      ]);
      const brandQuery = cardBrandId ? `?masterId=${encodeURIComponent(cardBrandId)}` : "";
      toast.success("Cargas confirmadas. Revisá los movimientos antes de conciliar.");
      onClose();
      navigate(`/tarjetas/liquidaciones${brandQuery}`);
    },
    onError: (error) => {
      setMessage(error.message);
      toast.error(error, { fallback: "No se pudieron confirmar las cargas." });
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: ["new-import", "CARD", ledgerBatchId] }),
        queryClient.invalidateQueries({ queryKey: ["new-import", "CARD", creditBatchId] }),
        queryClient.invalidateQueries({ queryKey: ["new-import", "CARD", debitBatchId] }),
      ]);
    },
  });
  const retrySlot = async (slot: CardImportSlot, replace = false) => {
    setRetryingSlot(slot);
    setSlotErrors((current) => ({ ...current, [slot]: "" }));
    setSlotConflicts((current) => ({ ...current, [slot]: false }));
    try {
      const batch = await uploadSlot(slot, replace);
      if (slot === "ledger") setLedgerBatchId(batch.id);
      if (slot === "credit") setCreditBatchId(batch.id);
      if (slot === "debit") setDebitBatchId(batch.id);
      setMessage(`${slot === "ledger" ? "Libro Mayor" : slot === "credit" ? "Liquidación crédito" : "Liquidación débito"} recibido. Estamos preparando la vista previa.`);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["domain-imports", "CARD"] }),
        queryClient.invalidateQueries({ queryKey: ["workflow", "CARD"] }),
        queryClient.invalidateQueries({ queryKey: ["card-brand-overview"] }),
      ]);
    } catch (error) {
      const uploadError = error instanceof Error ? error : new Error("No se pudo cargar el archivo.");
      const apiCode = (uploadError as Error & { code?: string }).code;
      setSlotErrors((current) => ({ ...current, [slot]: uploadError.message }));
      setSlotConflicts((current) => ({ ...current, [slot]: apiCode === "CONFLICT" || uploadError.message.includes("Ya existe una carga") }));
    } finally {
      setRetryingSlot("");
    }
  };
  const replaceSlot = async (slot: CardImportSlot) => {
    const label = slot === "ledger" ? "el Libro Mayor" : slot === "credit" ? "la liquidación de crédito" : "la liquidación de débito";
    const accepted = await confirmDialog({
      title: `Reemplazar ${label}`,
      description: `Se reemplazará únicamente ${label}. Los otros archivos válidos se conservarán y las conciliaciones que usen este archivo se desvincularán.`,
      confirmLabel: "Reemplazar archivo",
      tone: "danger",
    });
    if (accepted) await retrySlot(slot, true);
  };
  const batches = [ledgerBatch.data, creditBatch.data, debitBatch.data].filter(Boolean);
  const active = create.isPending || confirmLoads.isPending || Boolean(retryingSlot) || batches.some((batch) => ["QUEUED", "PROCESSING"].includes(batch?.status ?? ""));
  const selectionLocked = active || Boolean(ledgerBatchId || creditBatchId || debitBatchId);
  const activeRef = useRef(active); activeRef.current = active;
  const close = () => { if (!activeRef.current) { onClose(); return; } void confirmDialog({ title: "Cerrar la carga en curso", description: "Los archivos continuarán procesándose en segundo plano. Podrás volver al historial para ver la vista previa.", confirmLabel: "Cerrar igual" }).then((accepted) => { if (accepted) onClose(); }); };
  useEffect(() => {
    if (open) return;
    setImportPeriod(previousCalendarMonth());
    setCardBrandId("");
    setLedgerFile(undefined);
    setCreditFile(undefined);
    setDebitFile(undefined);
    setFileError("");
    setLedgerBatchId("");
    setCreditBatchId("");
    setDebitBatchId("");
    setMessage("");
    setSlotErrors({ ledger: "", credit: "", debit: "" });
    setSlotConflicts({ ledger: false, credit: false, debit: false });
    setRetryingSlot("");
    setShowPreview(false);
    create.reset();
    confirmLoads.reset();
  }, [open]);
  useEffect(() => {
    if (!open || !lockedBrandId) return;
    setCardBrandId(lockedBrandId);
  }, [lockedBrandId, open]);
  useEffect(() => { if (!open) return; returnFocus.current = document.activeElement as HTMLElement; const previousOverflow = document.body.style.overflow; document.body.style.overflow = "hidden"; requestAnimationFrame(() => firstField.current?.focus()); const keydown = (event: KeyboardEvent) => { if (event.key === "Escape") close(); if (event.key !== "Tab") return; const focusable = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), select:not(:disabled), input:not(:disabled), a[href]') ?? []); if (!focusable.length) return; const first = focusable[0]; const last = focusable[focusable.length - 1]; if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); } else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); } }; document.addEventListener("keydown", keydown); return () => { document.body.style.overflow = previousOverflow; document.removeEventListener("keydown", keydown); returnFocus.current?.focus(); }; }, [open]);
  if (!open) return null;
  const readyToConfirmCount = [ledgerBatch.data, creditBatch.data, debitBatch.data].filter((batch) => batch?.status === "PREVIEW").length;
  const canConfirm = readyToConfirmCount > 0;
  const pendingUploadCount = Number(Boolean(ledgerFile && !ledgerBatchId)) + Number(Boolean(creditFile && !creditBatchId)) + Number(Boolean(debitFile && !debitBatchId));
  const statusMessage = ledgerBatch.data?.errorMessage || creditBatch.data?.errorMessage || debitBatch.data?.errorMessage || (canConfirm
    ? `${readyToConfirmCount === 1 ? "Vista previa lista" : "Vistas previas listas"}. Confirmá para incorporar ${readyToConfirmCount === 1 ? "el archivo" : "los archivos"}.`
    : message);
  const year = Number(importPeriod.slice(0, 4));
  const month = Number(importPeriod.slice(5, 7));
  const years = [year - 1, year, year + 1];
  const acceptFile = (nextFile: File, extensions: string[]) => extensions.some((extension) => nextFile.name.toLowerCase().endsWith(extension));
  const acceptedFormatLabel = (extensions: string[]) => extensions.map((item) => item.slice(1).toUpperCase()).join(" o ");
  const chooseLedger = (nextFile?: File) => {
    if (!nextFile) return;
    if (!acceptFile(nextFile, ledgerExtensions)) { setFileError(`El archivo ${nextFile.name} no es válido. Elegí un archivo ${acceptedFormatLabel(ledgerExtensions)}.`); return; }
    setLedgerFile(nextFile); setFileError(""); setSlotErrors((current) => ({ ...current, ledger: "" })); setSlotConflicts((current) => ({ ...current, ledger: false }));
  };
  const chooseLiquidation = (funding: "CREDIT" | "DEBIT", nextFile?: File) => {
    if (!nextFile) return;
    if (!acceptFile(nextFile, liquidationExtensions)) { setFileError(`El archivo ${nextFile.name} no es válido. Elegí un archivo ${acceptedFormatLabel(liquidationExtensions)}.`); return; }
    const slot = funding === "CREDIT" ? "credit" : "debit";
    if (funding === "CREDIT") setCreditFile(nextFile);
    else setDebitFile(nextFile);
    setSlotErrors((current) => ({ ...current, [slot]: "" }));
    setSlotConflicts((current) => ({ ...current, [slot]: false }));
    setFileError("");
  };
  const selectCardBrand = (nextBrandId: string) => {
    setCardBrandId(nextBrandId);
    setLedgerFile(undefined);
    setCreditFile(undefined);
    setDebitFile(undefined);
    setFileError("");
    setMessage("");
    setSlotErrors({ ledger: "", credit: "", debit: "" });
    setSlotConflicts({ ledger: false, credit: false, debit: false });
  };
  const submit = (event: FormEvent) => { event.preventDefault(); create.mutate(); };
  const previewSummary = (batch?: ImportBatch, title?: string) => batch ? <div className="upload-progress" aria-live="polite"><span><strong>{title}: {batch.status === "PREVIEW" ? "Vista previa lista" : batch.status === "FAILED" ? "Archivo rechazado" : batch.status === "CONFIRMED" ? "Confirmada" : "Validando archivo"}</strong><small>{batch.totalRows} filas · {batch.validRows} válidas · {batch.errorRows} errores</small></span><progress max="100" value={batch.progress}/></div> : null;
  const slotIssue = (slot: CardImportSlot) => slotErrors[slot] ? <div className="card-file-slot__issue" role="alert">
    <span>{slotErrors[slot]}</span>
    <button type="button" className={slotConflicts[slot] ? "button button--danger" : "button"} disabled={active} onClick={() => void (slotConflicts[slot] ? replaceSlot(slot) : retrySlot(slot))}>
      {retryingSlot === slot ? "Procesando…" : slotConflicts[slot] ? "Reemplazar sólo este archivo" : "Reintentar este archivo"}
    </button>
  </div> : null;
  return <div className={`modal-backdrop${stacked ? " modal-backdrop--stacked" : ""}`} onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}><section ref={dialog} className={`import-modal${showPreview ? " import-modal--wide" : ""}`} role="dialog" aria-modal="true" aria-labelledby="new-import-title"><header><div><small>Nueva entrada · {importPeriod}</small><h2 id="new-import-title">Cargar archivos de tarjetas</h2></div><button type="button" className="modal-close" onClick={close} aria-label="Cerrar modal">×</button></header><form className="stack-form" onSubmit={submit}>
    <div className="form-row form-row--three">
      <label>Mes<select ref={firstField} value={String(month).padStart(2, "0")} disabled={selectionLocked} onChange={(event) => setImportPeriod(`${year}-${event.target.value}`)}>{MONTH_NAMES.map((name, index) => <option key={name} value={String(index + 1).padStart(2, "0")}>{name}</option>)}</select></label>
      <label>Año<select value={year} disabled={selectionLocked} onChange={(event) => setImportPeriod(`${event.target.value}-${String(month).padStart(2, "0")}`)}>{years.map((item) => <option key={item} value={item}>{item}</option>)}</select></label>
      <label>Marca{lockedBrandId ? " (fija)" : ""}<select value={cardBrandId} disabled={selectionLocked || Boolean(lockedBrandId)} title={lockedBrandId ? "La marca está fija porque estás trabajando en esta tarjeta." : undefined} onChange={(event) => selectCardBrand(event.target.value)}><option value="">Seleccionar marca</option>{(options.data?.brands ?? []).map((brand) => <option key={brand.id} value={brand.id}>{brand.name}</option>)}</select></label>
    </div>
    <div className="card-file-grid">
      <div className="card-file-group card-file-group--ledger">
        <CardFileDrop label="Libro Mayor" file={ledgerFile} extensions={ledgerExtensions} brandSelected={Boolean(selectedBrand)} disabled={active || Boolean(ledgerBatchId) || !selectedBrand || !ledgerExtensions.length} onChoose={chooseLedger} onClear={() => setLedgerFile(undefined)} />
        {previewSummary(ledgerBatch.data, "Libro Mayor")}
        {slotIssue("ledger")}
      </div>
      <div className="card-file-group">
        <CardFileDrop label="Liquidación crédito" file={creditFile} extensions={liquidationExtensions} brandSelected={Boolean(selectedBrand)} disabled={active || Boolean(creditBatchId) || !selectedBrand || !liquidationExtensions.length} onChoose={(file) => chooseLiquidation("CREDIT", file)} onClear={() => setCreditFile(undefined)} />
        {previewSummary(creditBatch.data, "Liquidación crédito")}
        {slotIssue("credit")}
      </div>
      <div className="card-file-group">
        <CardFileDrop label="Liquidación débito" file={debitFile} extensions={liquidationExtensions} brandSelected={Boolean(selectedBrand)} disabled={active || Boolean(debitBatchId) || !selectedBrand || !liquidationExtensions.length} onChoose={(file) => chooseLiquidation("DEBIT", file)} onClear={() => setDebitFile(undefined)} />
        {previewSummary(debitBatch.data, "Liquidación débito")}
        {slotIssue("debit")}
      </div>
    </div>
    {fileError && <div className="message message--error" role="alert">{fileError}</div>}
    {statusMessage && <div className={create.isError || confirmLoads.isError || ledgerBatch.data?.status === "FAILED" || creditBatch.data?.status === "FAILED" || debitBatch.data?.status === "FAILED" ? "message message--error" : "message message--success"}>{statusMessage}</div>}
    {canConfirm && <div className="form-row">
      <button type="button" className="button" onClick={() => setShowPreview((value) => !value)}>{showPreview ? "Ocultar vista previa" : "Ver vista previa"}</button>
      <button type="button" className="button button--primary" disabled={confirmLoads.isPending} onClick={() => confirmLoads.mutate()}>{confirmLoads.isPending ? "Confirmando…" : readyToConfirmCount === 1 ? "Confirmar archivo" : "Confirmar archivos listos"}</button>
    </div>}
    {showPreview && <div className="card-import-preview" aria-label="Vista previa de archivos">
      {ledgerBatchId && <Panel title="Libro Mayor"><NormalizedDataTable rows={ledgerPreviewRows.data?.items ?? []} domain="CARD" /></Panel>}
      {creditBatchId && <Panel title="Liquidación crédito"><NormalizedDataTable rows={creditPreviewRows.data?.items ?? []} domain="CARD" /></Panel>}
      {debitBatchId && <Panel title="Liquidación débito"><NormalizedDataTable rows={debitPreviewRows.data?.items ?? []} domain="CARD" /></Panel>}
    </div>}
    <button className="button button--primary" disabled={active || pendingUploadCount === 0}>{active ? "Procesando…" : pendingUploadCount === 0 ? "Adjuntá al menos un archivo" : `Cargar ${pendingUploadCount} ${pendingUploadCount === 1 ? "archivo" : "archivos"} y validar`}</button>
  </form></section></div>;
}

export function NewImportModal({ config, open, stacked = false, onClose, onPreviewReady }: { config: ImportWorkspaceConfig; open: boolean; stacked?: boolean; onClose(): void; onPreviewReady?(id: string): void }) {
  if (config.domain === "CARD") return <CardDualImportModal open={open} stacked={stacked} onClose={onClose} onPreviewReady={onPreviewReady} />;
  return <BankOrOtaImportModal config={config} open={open} stacked={stacked} onClose={onClose} onPreviewReady={onPreviewReady} />;
}

function BankOrOtaImportModal({ config, open, stacked = false, onClose, onPreviewReady }: { config: ImportWorkspaceConfig; open: boolean; stacked?: boolean; onClose(): void; onPreviewReady?(id: string): void }) {
  const confirmDialog = useConfirmDialog();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { hotelId, period } = useWorkspace();
  const [params] = useSearchParams();
  const masterId = params.get("masterId") ?? "";
  const filterBankId = params.get("bankId") ?? (config.domain === "BANK" ? masterId : "");
  const filterCurrency = params.get("currency") ?? "";
  const contextSearch = config.domain === "BANK" ? bankSearch(filterBankId, filterCurrency) : masterSearch(masterId);
  const firstField = useRef<HTMLSelectElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const dialog = useRef<HTMLElement>(null);
  const [accountId, setAccountId] = useState("");
  const [sourceId, setSourceId] = useState("");
  const [bankId, setBankId] = useState("");
  const [currency, setCurrency] = useState<BankCurrency | "">("");
  const [loadType, setLoadType] = useState<"EXTRACTO" | "LIBRO_MAYOR" | "">("");
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [canReplace, setCanReplace] = useState(false);
  const [file, setFile] = useState<File>();
  const [fileError, setFileError] = useState("");
  const [draggingFile, setDraggingFile] = useState(false);
  const [batchId, setBatchId] = useState("");
  const [message, setMessage] = useState("");
  const isBank = config.domain === "BANK";
  const sources = useQuery({ queryKey: ["source-options", config.domain], queryFn: (): Promise<Source[]> => responseData(apiClient.GET("/api/v1/cards/imports/sources")), enabled: open && !isBank });
  const bankOptions = useQuery({ queryKey: ["bank-import-options"], queryFn: () => responseData(apiClient.GET("/api/v1/banking/imports/configuration")), enabled: open && isBank });
  const selectedBank = bankOptions.data?.banks.find((bank) => bank.id === bankId);
  const availableCurrencies = selectedBank?.accounts.map((account) => account.currency) ?? [];
  const enabledCurrencies = [...new Set(selectedBank?.accounts.filter((account) => account.active).map((account) => account.currency) ?? [])];
  const source = sources.data?.find((item) => item.id === sourceId);
  const targetKind = isBank ? "NONE" : (source?.targetKind ?? (config.accountKind === "BANK" ? "BANK_ACCOUNT" : "ACCOUNTING_ACCOUNT"));
  const accounts = useQuery({ queryKey: ["account-options", hotelId, targetKind], queryFn: async (): Promise<Account[]> => (await responseData(apiClient.GET("/api/v1/cards/accounts", { params: { query: { hotelId, kind: targetKind } } }))).map((item) => ({ ...item, hotelId, institution: "", accountNumber: "", currency: item.currency ?? "", kind: item.kind === "BANK" || item.kind === "CARD" || item.kind === "OTA" || item.kind === "CORPORATE" ? item.kind : "CORPORATE" })), enabled: open && !isBank && Boolean(hotelId) && targetKind !== "NONE" });
  const batch = useQuery({ queryKey: ["new-import", config.domain, batchId], queryFn: () => importDetail(config.domain, batchId), enabled: open && Boolean(batchId), refetchInterval: (query) => ["QUEUED", "PROCESSING"].includes(query.state.data?.status ?? "") ? 1500 : false });
  const create = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Seleccioná un archivo.");
      const form = new FormData();
      form.set("file", file); form.set("hotelId", hotelId); form.set("period", period);
      if (isBank) {
        if (!bankId || !currency || !loadType) throw new Error("Seleccioná el tipo de archivo, el banco y la moneda.");
        form.set("bankId", bankId); form.set("currency", currency); form.set("loadType", loadType); if (replaceExisting) form.set("replaceExisting", "true");
      } else {
        if (!sourceId || (targetKind !== "NONE" && !accountId)) throw new Error("Seleccioná fuente, destino y archivo.");
        if (accountId) form.set("accountId", accountId); form.set("sourceDefinitionId", sourceId);
      }
      return isBank
        ? responseData(apiClient.POST("/api/v1/banking/imports", {
            body: { hotelId, period, bankId, currency: currency || "ARS", loadType, replaceExisting: replaceExisting ? "true" : undefined, file: file.name },
            bodySerializer: () => form,
          }))
        : responseData(apiClient.POST("/api/v1/cards/imports", {
            body: { hotelId, period, sourceDefinitionId: sourceId, accountId: accountId || undefined, file: file.name },
            bodySerializer: () => form,
          }));
    },
    onSuccess: async (created) => { setBatchId(created.id); setMessage("Archivo recibido. Estamos preparando la vista previa."); setCanReplace(false); await Promise.all([queryClient.invalidateQueries({ queryKey: ["domain-imports", config.domain] }), queryClient.invalidateQueries({ queryKey: ["workflow", config.domain] })]); },
    onError: (error) => {
      const apiCode = (error as Error & { code?: string }).code;
      const isConflict = apiCode === "CONFLICT" || error.message.includes("Ya existe una carga");
      setMessage(error.message);
      setCanReplace(isBank && isConflict);
    }
  });
  const active = create.isPending || ["QUEUED", "PROCESSING"].includes(batch.data?.status ?? "");
  const activeRef = useRef(active); activeRef.current = active;
  const close = () => { if (!activeRef.current) { onClose(); return; } void confirmDialog({ title: "Cerrar la carga en curso", description: "El archivo continuará procesándose en segundo plano. Podrás volver al historial para ver la vista previa.", confirmLabel: "Cerrar igual" }).then((accepted) => { if (accepted) onClose(); }); };
  useEffect(() => {
    if (open) return;
    setAccountId("");
    setSourceId("");
    setBankId("");
    setCurrency("");
    setLoadType("");
    setReplaceExisting(false);
    setCanReplace(false);
    setFile(undefined);
    setFileError("");
    setDraggingFile(false);
    setBatchId("");
    setMessage("");
    create.reset();
  }, [open]);
  useEffect(() => { if (!open) return; returnFocus.current = document.activeElement as HTMLElement; const previousOverflow = document.body.style.overflow; document.body.style.overflow = "hidden"; requestAnimationFrame(() => firstField.current?.focus()); const keydown = (event: KeyboardEvent) => { if (event.key === "Escape") close(); if (event.key !== "Tab") return; const focusable = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), select:not(:disabled), input:not(:disabled), a[href]') ?? []); if (!focusable.length) return; const first = focusable[0]; const last = focusable[focusable.length - 1]; if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); } else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); } }; document.addEventListener("keydown", keydown); return () => { document.body.style.overflow = previousOverflow; document.removeEventListener("keydown", keydown); returnFocus.current?.focus(); }; }, [open]);
  useEffect(() => { if (!open || !isBank || !bankOptions.data) return; const selectedBank = bankOptions.data.banks.find((bank) => bank.id === filterBankId); if (!selectedBank) { setBankId(""); setCurrency(""); return; } const enabledCurrencies = [...new Set(selectedBank.accounts.filter((account) => account.active).map((account) => account.currency))]; setBankId(selectedBank.id); setCurrency(selectedBank.accounts.some((account) => account.currency === filterCurrency) ? filterCurrency as BankCurrency : enabledCurrencies.length === 1 ? enabledCurrencies[0] : ""); }, [bankOptions.data, filterBankId, filterCurrency, isBank, open]);
  useEffect(() => {
    if (!open || batch.data?.status !== "PREVIEW") return;
    onClose();
    if (onPreviewReady) onPreviewReady(batch.data.id);
    else navigate(`${config.basePath}/cargas/${batch.data.id}${contextSearch}`);
  }, [batch.data?.id, batch.data?.status, config.basePath, contextSearch, navigate, onClose, onPreviewReady, open]);
  if (!open) return null;
  const submit = (event: FormEvent) => { event.preventDefault(); setReplaceExisting(false); setCanReplace(false); create.mutate(); };
  const replace = async () => { const accepted = await confirmDialog({ title: "Reemplazar carga existente", description: isBank ? "La carga actual y sus movimientos o asientos se eliminarán, aunque el archivo sea idéntico. Las conciliaciones que los usen se desvincularán, sus contrapartes volverán a quedar pendientes y el archivo se validará nuevamente para abrir una nueva vista previa." : "La carga actual y sus movimientos se eliminarán. La conciliación del período deberá ejecutarse nuevamente.", confirmLabel: "Reemplazar carga", tone: "danger" }); if (accepted) { setReplaceExisting(true); setCanReplace(false); setTimeout(() => create.mutate(), 0); } };
  const selectBank = (nextBankId: string) => {
    const nextBank = bankOptions.data?.banks.find((bank) => bank.id === nextBankId);
    const nextEnabledCurrencies = [...new Set(nextBank?.accounts.filter((account) => account.active).map((account) => account.currency) ?? [])];
    setBankId(nextBankId);
    if (nextEnabledCurrencies.length === 1) {
      setCurrency(nextEnabledCurrencies[0]);
      return;
    }
    if (!nextBank?.accounts.some((account) => account.currency === currency)) setCurrency("");
  };
  const acceptedExtensions = isBank
    ? loadType === "EXTRACTO" ? [".pdf"] : loadType === "LIBRO_MAYOR" ? [".xlsx"] : []
    : source?.fileExtensions.map((extension) => extension.startsWith(".") ? extension.toLowerCase() : `.${extension.toLowerCase()}`) ?? [];
  const acceptedTypes = acceptedExtensions.join(",");
  const acceptsFile = (nextFile: File, extensions = acceptedExtensions) => extensions.some((extension) => nextFile.name.toLowerCase().endsWith(extension));
  const chooseFile = (nextFile?: File) => {
    if (!nextFile) return;
    if (!acceptedExtensions.length) {
      setFileError(isBank ? "Seleccioná el tipo de archivo antes de elegir un archivo." : "Seleccioná la fuente antes de elegir un archivo.");
      return;
    }
    if (!acceptsFile(nextFile)) {
      const formats = acceptedExtensions.map((extension) => extension.slice(1).toUpperCase()).join(" o ");
      setFileError(`El archivo ${nextFile.name} no es válido. Elegí un archivo ${formats}.`);
      return;
    }
    setFile(nextFile);
    setFileError("");
  };
  const selectLoadType = (nextLoadType: "EXTRACTO" | "LIBRO_MAYOR" | "") => {
    const nextExtensions = nextLoadType === "EXTRACTO" ? [".pdf"] : nextLoadType === "LIBRO_MAYOR" ? [".xlsx"] : [];
    setLoadType(nextLoadType);
    setFileError("");
    if (file && !acceptsFile(file, nextExtensions)) setFile(undefined);
  };
  const startFileDrag = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = active ? "none" : "copy";
    if (!active) setDraggingFile(true);
  };
  const leaveFileDrag = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
    setDraggingFile(false);
  };
  const dropFile = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDraggingFile(false);
    if (!active) chooseFile(event.dataTransfer.files[0]);
  };
  return <div className={`modal-backdrop${stacked ? " modal-backdrop--stacked" : ""}`} onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}><section ref={dialog} className="import-modal" role="dialog" aria-modal="true" aria-labelledby="new-import-title"><header><div><small>Nueva entrada · {period}</small><h2 id="new-import-title">Cargar archivo de {config.noun}</h2></div><button type="button" className="modal-close" onClick={close} aria-label="Cerrar modal">×</button></header><form className="stack-form" onSubmit={submit}>{isBank ? <div className="form-row form-row--three"><label>Tipo de archivo<select ref={firstField} value={loadType} disabled={active} onChange={(event) => selectLoadType(event.target.value as "EXTRACTO" | "LIBRO_MAYOR" | "")}><option value="">Seleccionar tipo</option>{bankOptions.data?.loadTypes.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label><label>Banco<select value={bankId} disabled={active} onChange={(event) => selectBank(event.target.value)}><option value="">Seleccionar banco</option>{bankOptions.data?.banks.map((bank) => <option key={bank.id} value={bank.id}>{bank.name}</option>)}</select></label><label>Moneda<select value={currency} disabled={active || !bankId} onChange={(event) => setCurrency(event.target.value as BankCurrency | "")}><option value="">Seleccionar moneda</option>{availableCurrencies.map((item) => <option key={item} value={item}>{item === "ARS" ? "Pesos (ARS)" : "Dólares (USD)"}</option>)}</select></label></div> : <><label>Fuente<select ref={firstField} value={sourceId} disabled={active} onChange={(event) => setSourceId(event.target.value)}><option value="">Seleccionar fuente</option>{sources.data?.map((item) => <option key={item.id} value={item.id}>{item.name} · {item.parserVersion}</option>)}</select></label>{targetKind !== "NONE" && <label>Cuenta contable<select value={accountId} disabled={active} onChange={(event) => setAccountId(event.target.value)}><option value="">Seleccionar destino</option>{accounts.data?.map((item) => <option key={item.id} value={item.id}>{item.name} · {item.currency}</option>)}</select></label>}</>}<label className={`drop-zone${file ? " drop-zone--ready" : ""}${draggingFile ? " drop-zone--dragging" : ""}`} aria-disabled={active} onDragEnter={startFileDrag} onDragOver={startFileDrag} onDragLeave={leaveFileDrag} onDrop={dropFile}><input type="file" disabled={active} accept={acceptedTypes || undefined} onChange={(event) => { chooseFile(event.target.files?.[0]); event.currentTarget.value = ""; }}/><span className="drop-zone__icon">↑</span><strong>{file?.name ?? `Elegir archivo de ${config.noun}`}</strong><small>{acceptedTypes ? `${acceptedTypes.replaceAll(".", "").toUpperCase()} · o arrastralo aquí` : "Elegí un tipo para ver formatos"}</small></label>{fileError && <div className="message message--error" role="alert">{fileError}</div>}{batch.data && <div className="upload-progress" aria-live="polite"><span><strong>{batch.data.status === "PROCESSING" ? "Validando archivo" : batch.data.status === "FAILED" ? "Archivo rechazado" : "Carga en cola"}</strong><small>{batch.data.progress}% · {batch.data.totalRows} filas detectadas</small></span><progress max="100" value={batch.data.progress}/></div>}{message && <div className={create.isError || batch.data?.status === "FAILED" ? "message message--error" : "message message--success"}>{batch.data?.errorMessage || message}</div>}{canReplace && <button type="button" className="button button--danger" onClick={() => void replace()}>Reemplazar carga existente</button>}<button className="button button--primary" disabled={active}>{active ? "Procesando…" : "Cargar y validar"}</button></form></section></div>;
}
export function ImportDetail({ config, id: providedId, modal = false, onBack }: { config: ImportWorkspaceConfig; id?: string; modal?: boolean; onBack?(): void }) {
  const { id: routeId = "" } = useParams();
  const id = providedId ?? routeId;
  const navigate = useNavigate();
  const toast = useToast();
  const confirmDialog = useConfirmDialog();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const rowPageKey = modal ? "importRowPage" : "rowPage";
  const rowPage = Number(params.get(rowPageKey) ?? 1);
  const masterId = params.get("masterId") ?? "";
  const bankId = params.get("bankId") ?? (config.domain === "BANK" ? masterId : "");
  const currency = params.get("currency") ?? "";
  const contextSearch = config.domain === "BANK" ? bankSearch(bankId, currency) : masterSearch(masterId);
  const detail = useQuery({
    queryKey: ["domain-import", config.domain, id],
    queryFn: () => importDetail(config.domain, id),
    refetchInterval: (query) => ["QUEUED", "PROCESSING"].includes(query.state.data?.status ?? "") ? 2000 : false
  });
  const rows = useQuery({
    queryKey: ["domain-import-rows", id, rowPage, masterId, bankId, currency],
    queryFn: () => importRows(config.domain, id, rowPage),
    enabled: Boolean(detail.data)
  });
  const removeFailed = useMutation({
    mutationFn: () => deleteImport(config.domain, id),
    onSuccess: async () => {
      toast.success("Carga fallida eliminada.");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["domain-imports", config.domain] }),
        queryClient.invalidateQueries({ queryKey: ["workflow", config.domain] })
      ]);
      if (onBack) onBack();
      else navigate(`${config.basePath}/cargas${contextSearch}`);
    },
    onError: (reason) => toast.error(reason, { fallback: "No se pudo eliminar la carga fallida." })
  });
  const batch = detail.data;
  if (detail.isError) return <div className="empty-state"><h1>No se pudo abrir la carga</h1><p>{detail.error.message}</p></div>;
  if (!batch) return <div className="empty-state"><h1>Cargando lote</h1></div>;
  const isConfirmed = ["CONFIRMED", "CONCILIATED"].includes(batch.status);
  const setPage = (page: number) => { const next = new URLSearchParams(params); next.set(rowPageKey, String(page)); setParams(next); };
  return (
    <>
      {!modal && <div className="detail-heading">
        <Link className="back-link" to={`${config.basePath}/cargas${contextSearch}`}>← Volver a cargas</Link>
        <span className={`status status--${statusClass(batch.status)}`}>{batch.status}</span>
      </div>}
      {!modal && <section className="import-dossier">
        <div><span>{batch.sourceDefinition.name}{batch.bank ? ` · ${batch.bank.name} · ${batch.currency ?? "ARS"}` : ""}</span><h2>{batch.sourceFile?.originalName ?? "Lote"}</h2></div>
        <dl><div><dt>Movimientos efectivos</dt><dd>{batch.totalRows}</dd></div></dl>
      </section>}
      {batch.status === "FAILED" && <div className="confirm-bar confirm-bar--error"><span><strong>La carga fue rechazada</strong><small>{batch.errorMessage || "El archivo no superó las validaciones."}</small></span><button className="button button--danger" onClick={() => void confirmDialog({ title: "Eliminar carga fallida", description: `Se eliminará ${batch.sourceFile?.originalName ?? "esta carga"} y su diagnóstico. Esta acción no se puede deshacer.`, confirmLabel: "Eliminar carga", tone: "danger" }).then((accepted) => { if (accepted) removeFailed.mutate(); })} disabled={removeFailed.isPending}>Eliminar carga fallida</button></div>}
      {!modal && batch.status === "PREVIEW" && <ImportConfirmationAction config={config} id={id} variant="body" />}
      <div className={modal ? undefined : "content-grid content-grid--single"}>
        <Panel title={isConfirmed ? undefined : "Vista previa"} meta={isConfirmed ? undefined : `Parser ${batch.sourceFile?.parserVersion}`}>
          <ImportNettingSummary batch={batch} />
          <NormalizedDataTable rows={rows.data?.items ?? []} domain={config.domain} />
          <Pagination meta={rows.data?.meta} onPageChange={setPage} />
        </Panel>
      </div>
    </>
  );
}

export function LegacyImportRedirect() {
  const { id } = useParams();
  const navigate = useNavigate();
  const detail = useQuery({ queryKey: ["legacy-import", id], queryFn: async (): Promise<ImportBatch> => {
    const bank = await apiClient.GET("/api/v1/banking/imports/{id}", { params: { path: { id: id ?? "" } } });
    if (bank.data) return bank.data;
    return responseData(apiClient.GET("/api/v1/cards/imports/{id}", { params: { path: { id: id ?? "" } } }));
  }, enabled: Boolean(id) });
  useEffect(() => {
    if (!detail.data || !id) return;
    const base = detail.data.sourceDefinition.domain === "BANK" ? "/bancos" : detail.data.sourceDefinition.domain === "CARD" ? "/tarjetas" : "/cobranzas-ota";
    navigate(`${base}/cargas/${id}`, { replace: true });
  }, [detail.data, id, navigate]);
  if (!id) return <Navigate to="/bancos/cargas" replace />;
  return <div className="empty-state"><h1>Buscando carga</h1><p>Estamos abriendo el módulo correspondiente.</p></div>;
}
